// Earn Now Landing Page — creator-focused with hero card
'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { trackCampaignView, trackStartEarningClick, trackCreatorSignupStart } from '@/lib/tracking';
import { initializeTracking } from '@/lib/tracking';
import { Sparkles, Zap, Camera, Video, MessageCircle, Search, Shield, Users } from 'lucide-react';

export default function EarnNowPage() {
  useEffect(() => {
    initializeTracking();
    trackCampaignView('UGC');
  }, []);

  return (
    <div className="min-h-screen bg-[#080412]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div aria-hidden className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[20%] -right-[10%] w-[500px] h-[500px] rounded-full blur-[140px] bg-[#924890] opacity-20 animate-[hero-drift_14s_ease-in-out_infinite]" />
          <div className="absolute -bottom-[20%] -left-[15%] w-[450px] h-[450px] rounded-full blur-[130px] bg-[#E11A50] opacity-15 animate-[hero-drift-r_18s_ease-in-out_infinite]" />
          <div className="absolute top-[40%] left-[50%] w-[250px] h-[250px] rounded-full blur-[100px] bg-[#ECBA6A] opacity-10" />
        </div>
        {/* Noise */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[.04] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px',
          }}
        />

        <div className="relative z-10 max-w-lg mx-auto px-4 py-16 md:py-24">
          {/* Card */}
          <div className="relative">
            <div className="absolute -inset-[1px] rounded-2xl overflow-hidden opacity-50">
              <div className="absolute inset-0 w-[150%] h-[150%] -top-[25%] -left-[25%] animate-[hero-border-rotate_6s_linear_infinite]"
                style={{ background: 'conic-gradient(from 0deg, #ECBA6A, #E11A50, #924890, #3D1552, #924890, #E11A50, #ECBA6A)' }}
              />
            </div>
            <div className="relative rounded-2xl bg-[#0e0820]/80 backdrop-blur-xl border border-white/[.06] shadow-[0_24px_80px_-12px_rgba(146,72,144,.25)]">
              <div className="px-8 py-10 sm:px-10 sm:py-12 flex flex-col items-center text-center gap-6">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.2em] text-[#ECBA6A]/70">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>For Creators</span>
                  <Sparkles className="h-3.5 w-3.5" />
                </div>

                <h1 className="font-js text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight">
                  <span className="js-text-fill">Get Discovered.</span>
                  <br />
                  <span className="text-white">Get Booked.</span>
                </h1>

                <p className="max-w-sm text-[15px] leading-relaxed text-white/50">
                  Build your profile, upload your portfolio, and connect directly with producers and fans.
                </p>

                <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#ECBA6A]/40 to-transparent" />

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <Link
                    href="/register"
                    onClick={() => trackCreatorSignupStart()}
                    className="group relative inline-flex items-center justify-center gap-2 rounded-xl w-full sm:w-auto
                               px-7 py-3.5 font-bold text-[15px] text-white overflow-hidden
                               bg-gradient-to-r from-[#E11A50] via-[#EF5D7B] to-[#924890]
                               shadow-[0_8px_32px_-8px_rgba(225,26,80,.5)]
                               hover:shadow-[0_12px_40px_-8px_rgba(225,26,80,.6)]
                               hover:scale-[1.02] active:scale-[.98]
                               transition-all duration-300"
                  >
                    <Zap className="h-4 w-4" />
                    <span>Create Your Profile</span>
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  </Link>
                  <Link
                    href="/search"
                    className="inline-flex items-center justify-center rounded-xl w-full sm:w-auto
                               px-7 py-3.5 font-semibold text-[15px]
                               text-white/70 border border-white/10 bg-white/[.04]
                               hover:bg-white/[.08] hover:border-white/20 hover:text-white
                               transition-all duration-300"
                  >
                    See Who's Here
                  </Link>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 pt-1">
                  {[
                    { icon: Shield, label: 'Verified' },
                    { icon: Users, label: 'Free to Join' },
                  ].map(({ icon: Icon, label }) => (
                    <span key={label} className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-white/25">
                      <Icon className="h-3 w-3" />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get — dark cards */}
      <section className="py-16 px-4 border-t border-white/[.06]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10 text-white/90">What You Get</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Camera, title: 'Photo Galleries', desc: "Upload and organize your photos. Control what's public and what requires login." },
              { icon: Video, title: 'Video Uploads', desc: 'Upload videos that stream directly on the platform. Indexed and discoverable.' },
              { icon: MessageCircle, title: 'Direct Messaging', desc: 'Real-time chat with producers, agents, and collaborators.' },
              { icon: Search, title: 'Search Visibility', desc: 'Your profile is indexed by Google with structured data for organic discovery.' },
              { icon: Shield, title: 'ID Verification', desc: 'Verified badge for your profile. Required for explicit content uploads.' },
              { icon: Users, title: 'Classified Ads', desc: 'Post your availability, rates, and work preferences by city and category.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-xl bg-white/[.03] border border-white/[.06] p-5 hover:bg-white/[.06] hover:border-white/[.1] transition-all duration-300">
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#E11A50]/10 text-[#ECBA6A]">
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="font-semibold text-sm text-white/90">{title}</h3>
                </div>
                <p className="text-white/40 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-4 border-t border-white/[.06]">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-white/90">Start Building Your Presence</h2>
          <p className="text-white/40 mb-8">Free to join. No credit card required.</p>
          <Link
            href="/register"
            onClick={() => trackStartEarningClick()}
            className="group relative inline-flex items-center gap-2 rounded-xl
                       px-8 py-3.5 font-bold text-[15px] text-white overflow-hidden
                       bg-gradient-to-r from-[#E11A50] via-[#EF5D7B] to-[#924890]
                       shadow-[0_8px_32px_-8px_rgba(225,26,80,.5)]
                       hover:shadow-[0_12px_40px_-8px_rgba(225,26,80,.6)]
                       hover:scale-[1.02] active:scale-[.98]
                       transition-all duration-300"
          >
            <Zap className="h-4 w-4" />
            <span>Create Your Profile</span>
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </Link>
        </div>
      </section>
    </div>
  );
}
